import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";

/**
 * Hi-hat instrument using persistent oscillators for better performance and natural voice-stealing.
 *
 * This implementation keeps audio nodes alive and reuses them, which:
 * - Reduces GC pressure from constant node creation/destruction
 * - Naturally implements voice-stealing (new hits cut off previous ones)
 * - Provides smoother retriggering behavior
 */
export class HiHat extends Instrument {
  // Synthesis parameters
  private closedFilterFreq: number = 8000;
  private openFilterFreq: number = 6000;
  private closedDuration: number = 0.15; // Increased default closed duration
  private openDuration: number = 0.2;
  private minVelocityDuration: number = 0.05; // Duration at velocity 0
  private maxVelocityDuration: number = 0.25; // Duration at velocity 1
  private attackTime: number = 0.001;
  private openNoteThreshold: number = 46; // MIDI notes >= this are open hi-hat (46 = A#2)

  // Persistent audio nodes
  private noiseSource: AudioBufferSourceNode | null = null;
  private filter: BiquadFilterNode;
  private envelope: GainNode;
  private currentStopId: number = 0; // Used to cancel previous stop scheduling

  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);

    // Create persistent filter and envelope nodes
    this.filter = this.audioContext.createBiquadFilter();
    this.filter.type = "highpass";
    this.filter.frequency.value = this.closedFilterFreq;
    this.filter.Q.value = 2.0; // Fixed resonance for consistent sound

    this.envelope = this.audioContext.createGain();
    this.envelope.gain.value = 0;

    // Connect the persistent chain
    this.filter.connect(this.envelope);
    this.envelope.connect(this.gain);

    this.initializeParameters();
    this.createNoiseSource();
  }

  /**
   * Create a new noise source and connect it to the filter chain.
   */
  private createNoiseSource(): void {
    // Clean up existing source if any
    if (this.noiseSource) {
      this.noiseSource.disconnect();
    }

    // Create noise buffer - longer buffer for better quality
    const bufferSize = this.audioContext.sampleRate * 0.5; // 500ms of noise
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate shaped noise for more metallic character
    for (let i = 0; i < bufferSize; i++) {
      // Mix white noise with some higher frequency emphasis
      const whiteNoise = Math.random() * 2 - 1;
      const emphasis = Math.sin(i * 0.001) * 0.7; // Fixed brightness for consistent metallic character
      data[i] = whiteNoise * (1 + emphasis * 0.3);
    }

    // Create and configure new source
    this.noiseSource = this.audioContext.createBufferSource();
    this.noiseSource.buffer = buffer;
    this.noiseSource.loop = true;

    // Connect to persistent filter chain
    this.noiseSource.connect(this.filter);

    // Start the noise source immediately - it will be silent until envelope opens
    this.noiseSource.start();

    // Handle source ending (shouldn't happen due to looping, but just in case)
    this.noiseSource.onended = () => {
      console.warn("HiHat noise source ended unexpectedly");
    };
  }

  /**
   * Play a hi-hat note using persistent oscillators.
   */
  playNote(note: MusicalNote, startTime: number, _: number): void {
    const velocity = note.velocity ?? 1.0;

    // Determine if this should be an open or closed hi-hat based on note number
    const isOpen = note.noteNumber >= this.openNoteThreshold;
    const filterFreq = isOpen ? this.openFilterFreq : this.closedFilterFreq;

    // Calculate velocity-scaled duration
    const baseDuration = isOpen ? this.openDuration : this.closedDuration;
    const velocityScaledDuration =
      this.minVelocityDuration +
      (this.maxVelocityDuration - this.minVelocityDuration) * velocity;
    const hatDuration = Math.max(baseDuration, velocityScaledDuration);

    // Smoothly transition filter frequency to avoid clicks
    this.filter.frequency.setTargetAtTime(filterFreq, startTime, 0.002);

    // Cancel any previous envelope scheduling and hold current value
    this.currentStopId++;
    const thisStopId = this.currentStopId;
    this.envelope.gain.cancelAndHoldAtTime(startTime);

    // Ramp smoothly from current held value to new attack peak
    // No need for setValueAtTime(0) - we ramp from whatever the current value is
    this.envelope.gain.linearRampToValueAtTime(
      velocity,
      startTime + this.attackTime,
    );
    this.envelope.gain.exponentialRampToValueAtTime(
      0.00001,
      startTime + hatDuration,
    );
    // Use setTargetAtTime for smooth final silence instead of hard cutoff
    this.envelope.gain.setTargetAtTime(
      0,
      startTime + hatDuration,
      0.001, // Fast exponential approach to 0
    );
  }

  /**
   * Get valid MIDI notes for hi-hat.
   */
  getValidNotes(): number[] {
    return [42, 46]; // F#2 (closed) and A#2 (open) hi-hat notes
  }

  /**
   * Clean up persistent nodes when instrument is destroyed.
   */
  onDestroy(): void {
    if (this.noiseSource) {
      this.noiseSource.stop();
      this.noiseSource.disconnect();
    }
    this.filter.disconnect();
    this.envelope.disconnect();
    super.onDestroy();
  }

  /**
   * Initialize hi-hat parameters.
   */
  private initializeParameters(): void {
    this.addParameter(
      () => this.closedFilterFreq,
      (value) => {
        this.closedFilterFreq = value;
      },
      {
        key: "closedFilterFreq",
        label: "Closed Filter Freq",
        min: 4000,
        max: 12000,
        defaultValue: 8000,
        step: 200,
        unit: "Hz",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.openFilterFreq,
      (value) => {
        this.openFilterFreq = value;
      },
      {
        key: "openFilterFreq",
        label: "Open Filter Freq",
        min: 3000,
        max: 10000,
        defaultValue: 6000,
        step: 200,
        unit: "Hz",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.minVelocityDuration,
      (value) => {
        this.minVelocityDuration = value;
      },
      {
        key: "minVelocityDuration",
        label: "Min Velocity Duration",
        min: 0.01,
        max: 0.15,
        defaultValue: 0.05,
        step: 0.005,
        unit: "s",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.maxVelocityDuration,
      (value) => {
        this.maxVelocityDuration = value;
      },
      {
        key: "maxVelocityDuration",
        label: "Max Velocity Duration",
        min: 0.1,
        max: 0.5,
        defaultValue: 0.25,
        step: 0.005,
        unit: "s",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.closedDuration,
      (value) => {
        this.closedDuration = value;
      },
      {
        key: "closedDuration",
        label: "Closed Duration",
        min: 0.05,
        max: 0.4,
        defaultValue: 0.15,
        step: 0.01,
        unit: "s",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.openDuration,
      (value) => {
        this.openDuration = value;
      },
      {
        key: "openDuration",
        label: "Open Duration",
        min: 0.1,
        max: 1.0,
        defaultValue: 0.8,
        step: 0.01,
        unit: "s",
        category: "Hi-Hat",
      },
    );

    this.addParameter(
      () => this.attackTime,
      (value) => {
        this.attackTime = value;
      },
      {
        key: "attackTime",
        label: "Attack Time",
        min: 0.0001,
        max: 0.01,
        defaultValue: 0.001,
        step: 0.0001,
        unit: "s",
        category: "Hi-Hat",
      },
    );
  }
}
